import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Stack,
  Table,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { poApi } from "../../../api/po";
import { useVendorAuth } from "../../vendors/auth/VendorAuthContext";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  ISSUED: "blue",
  ACKNOWLEDGED: "green",
  CANCELLED: "red",
};

export function PODetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { vendor } = useVendorAuth();
  const queryClient = useQueryClient();

  const { data: po, isLoading } = useQuery({
    queryKey: ["purchase-order", id],
    queryFn: () => poApi.get(id!),
    enabled: !!id,
  });

  const issuePO = useMutation({
    mutationFn: () => poApi.issue(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-order", id] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      notifications.show({
        title: "Success",
        message: "PO issued successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to issue PO",
        color: "red",
      });
    },
  });

  const acknowledgePO = useMutation({
    mutationFn: () => poApi.acknowledge(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-order", id] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      notifications.show({
        title: "Success",
        message: "PO acknowledged successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to acknowledge PO",
        color: "red",
      });
    },
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading purchase order...</Text>
      </Container>
    );
  }

  if (!po) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red">Purchase order not found</Alert>
      </Container>
    );
  }

  const canIssue = po.status === "DRAFT";
  const canAcknowledge =
    po.status === "ISSUED" && vendor && po.vendorId === vendor.id;
  const items = Array.isArray(po.items) ? po.items : [];

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate("/purchase-orders")}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>Purchase Order</Title>
      </Group>

      <Stack gap="md">
        <Paper shadow="sm" p="md">
          <Group justify="space-between" mb="md">
            <Group>
              <Text fw={500}>PO Number:</Text>
              <Text>{po.poNumber}</Text>
            </Group>
            <Badge color={statusColors[po.status]} variant="light" size="lg">
              {po.status}
            </Badge>
          </Group>

          <Stack gap="sm">
            <Group>
              <Text fw={500} w={200}>
                RFQ:
              </Text>
              <Button
                variant="light"
                size="xs"
                onClick={() => navigate(`/rfq/${po.rfqId}`)}
              >
                View RFQ
              </Button>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Vendor:
              </Text>
              <Text>{po.vendor.businessName || po.vendor.email}</Text>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Total Amount:
              </Text>
              <Text fw={600} size="lg">
                {po.currency} {Number(po.totalAmount).toFixed(2)}
              </Text>
            </Group>
            {po.issuedAt && (
              <Group>
                <Text fw={500} w={200}>
                  Issued At:
                </Text>
                <Text>
                  {dayjs(po.issuedAt).format("DD MMM YYYY HH:mm")}
                </Text>
              </Group>
            )}
            {po.acknowledgedAt && (
              <Group>
                <Text fw={500} w={200}>
                  Acknowledged At:
                </Text>
                <Text>
                  {dayjs(po.acknowledgedAt).format("DD MMM YYYY HH:mm")}
                </Text>
              </Group>
            )}
            {po.issuedBy && (
              <Group>
                <Text fw={500} w={200}>
                  Issued By:
                </Text>
                <Text>{po.issuedBy.fullName}</Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Items
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Quantity</Table.Th>
                <Table.Th>Unit</Table.Th>
                <Table.Th>Unit Price</Table.Th>
                <Table.Th>Total Price</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item: any, index: number) => (
                <Table.Tr key={index}>
                  <Table.Td>{item.itemName}</Table.Td>
                  <Table.Td>{item.description || "-"}</Table.Td>
                  <Table.Td>{item.quantity}</Table.Td>
                  <Table.Td>{item.unit}</Table.Td>
                  <Table.Td>
                    {po.currency} {item.unitPrice?.toFixed(2) || "0.00"}
                  </Table.Td>
                  <Table.Td>
                    {po.currency} {item.totalPrice?.toFixed(2) || "0.00"}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Group justify="flex-end">
          {canIssue && (
            <Button
              onClick={() => issuePO.mutate()}
              loading={issuePO.isPending}
            >
              Issue PO
            </Button>
          )}
          {canAcknowledge && (
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={() => acknowledgePO.mutate()}
              loading={acknowledgePO.isPending}
            >
              Acknowledge PO
            </Button>
          )}
        </Group>
      </Stack>
    </Container>
  );
}

